import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Check, Truck } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { RefundStatusBadge } from '../components/RefundStatusBadge'
import { REFUND_TYPE } from '@/schemas/supplier'
import { useSupplierRefund, useRefundConfirmReceive } from '@/hooks/useSupplierRefund'
import { showError } from '@/lib/error'

export function RefundDetailPage() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const refundId = Number(id)

  const { data: refund, isLoading } = useSupplierRefund(refundId)
  const confirmReceiveMutation = useRefundConfirmReceive()

  const [showDeliveryInfoDialog, setShowDeliveryInfoDialog] = useState(false)
  const [showConfirmReceiveDialog, setShowConfirmReceiveDialog] = useState(false)

  const canConfirmReceive = refund?.refund_type === REFUND_TYPE.RETURNING

  const handleDeliveryInfo = () => {
    setShowDeliveryInfoDialog(true)
  }

  const handleConfirmReceive = () => {
    setShowConfirmReceiveDialog(true)
  }

  const handleConfirmReceiveSubmit = () => {
    confirmReceiveMutation.mutate(refundId, {
      onSuccess: () => {
        setShowConfirmReceiveDialog(false)
        setShowDeliveryInfoDialog(false)
      },
      onError: (error) => showError(error, '确认收货失败'),
    })
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/supplier/refunds')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <Skeleton className="h-8 w-32" />
        </div>
        <div className="space-y-6">
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    )
  }

  if (!refund) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <p className="text-muted-foreground mb-4">退款不存在</p>
        <Button onClick={() => navigate('/supplier/refunds')}>
          返回退款列表
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/supplier/refunds')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">退款详情</h1>
            <p className="text-muted-foreground">退款单号： {refund.order_id}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canConfirmReceive && (
            <Button onClick={handleConfirmReceive}>
              <Check className="mr-1.5 h-4 w-4" />
              确认收货
            </Button>
          )}
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">退款状态：</span>
            <RefundStatusBadge refundStatus={refund.refund_type} />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">退款金额：</span>
            <span className="text-lg font-semibold text-red-500">
              ¥{refund.refund_price.toFixed(2)}
            </span>
          </div>
          {refund.refund_reason && (
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">退款原因：</span>
              <span>{refund.refund_reason}</span>
            </div>
          )}
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">申请时间：</span>
            <span>{refund.add_time}</span>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">收货人：</span>
            <span>{refund.order_info?.real_name || '-'}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">联系电话：</span>
            <span>{refund.order_info?.user_phone || '-'}</span>
          </div>
        </div>
      </div>

      {refund.delivery_name && (
        <div className="rounded-lg border p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">{refund.delivery_name}</p>
              <p className="text-sm text-muted-foreground">运单号： {refund.delivery_id}</p>
            </div>
            <Button variant="outline" size="sm" onClick={handleDeliveryInfo}>
              <Truck className="mr-1.5 h-4 w-4" />
              查看物流
            </Button>
          </div>
        </div>
      )}

      <div className="space-y-4">
        <h2 className="text-lg font-semibold">商品信息</h2>
        <div className="space-y-3">
          {refund.cart_info?.map((item) => (
            <div key={item.cart_id} className="flex items-start gap-4 rounded-lg border p-3">
              <img
                src={item.product_image}
                alt={item.product_name}
                className="h-16 w-16 rounded object-cover"
              />
              <div className="flex-1">
                <p className="font-medium">{item.product_name}</p>
                {item.product_attr && (
                  <p className="text-sm text-muted-foreground">{item.product_attr}</p>
                )}
                <div className="mt-1 flex items-center gap-4">
                  <span className="text-sm">¥{item.price.toFixed(2)}</span>
                  <span className="text-sm text-muted-foreground">x{item.cart_num}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {showDeliveryInfoDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="rounded-lg bg-white p-6 shadow-lg">
            <h3 className="text-lg font-semibold">物流信息</h3>
            <div className="mt-4 space-y-2">
              <p><span className="text-muted-foreground">快递公司：</span> {refund.delivery_name}</p>
              <p><span className="text-muted-foreground">运单号：</span> {refund.delivery_id}</p>
            </div>
            <div className="mt-6 flex justify-end">
              <Button variant="outline" onClick={() => setShowDeliveryInfoDialog(false)}>
                关闭
              </Button>
            </div>
          </div>
        </div>
      )}

      {showConfirmReceiveDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="rounded-lg bg-white p-6 shadow-lg">
            <h3 className="text-lg font-semibold">确认收货</h3>
            <p className="mt-2 text-muted-foreground">确认已收到退货商品后将自动退款给买家</p>
            <div className="mt-6 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowConfirmReceiveDialog(false)}>
                取消
              </Button>
              <Button onClick={handleConfirmReceiveSubmit} disabled={confirmReceiveMutation.isPending}>
                {confirmReceiveMutation.isPending ? '处理中...' : '确认'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default RefundDetailPage
